// Copyright 2016 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    BusinessUnitDTO,
    OwnedModule as CreatedModule,
    FrontEndConnectorDTO,
    FrontEndDTO,
    HardwareModule,
    MicrophoneArrayDTO,
    MicrophoneArrayRackDTO,
    ModuleTypes,
    MotionDeviceDTO,
    NewFrontEndConnector,
    NewMicrophoneArrayRack,
    NewMotionDevice,
    NewWebcam,
    OwnedHardwareModuleDTOOfFrontEndConnectorDTO,
    OwnedHardwareModuleDTOOfMicrophoneArrayRackDTO,
    OwnedHardwareModuleDTOOfMotionDeviceDTO,
    OwnedHardwareModuleDTOOfWebcamDTO,
    WebcamDTO,
} from "datatypes/SoramaWebApi";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { BusinessUnitRepository } from "repositories/BusinessUnitRepository";
import { Repository } from "repositories/Repository";

//#region Base Classes
export class OwnedModule<T> {
    constructor(module: T, ownerId: string) {
        this.ownerId = ownerId;
        this.reference = module;
    }

    public reference: T;
    public ownerId: string;
}

export class ModuleRepository extends Repository {
    protected webRequestHandler: IWebRequestHandler;
    protected businessUnitRepository: BusinessUnitRepository;

    constructor(webRequestHandler: IWebRequestHandler, businessUnitRepository: BusinessUnitRepository) {
        super();
        this.webRequestHandler = webRequestHandler;
        this.businessUnitRepository = businessUnitRepository;
    }

    public changeOwnership(moduleId: string, businessUnitId: string): Promise<void> {
        return this.webRequestHandler
            .createPutRequest<void>(Api.hardware + "/" + moduleId + "/Owner", businessUnitId)
            .withValue()
            .send()
            .then(() => {})
            .catch((e) => this.onError(e));
    }

    public getOwner(moduleId: string): Promise<BusinessUnitDTO> {
        return this.webRequestHandler
            .createGetRequest<string>(Api.hardware + "/" + moduleId + "/Owner")
            .send()
            .then((response) => this.businessUnitRepository.getBusinessUnit(response.content))
            .catch((e) => this.onError(e));
    }
}

@autoinject
export class HardwareModuleRepository<TModRef, TOwnedModRef, TNewMod> extends ModuleRepository {
    private hardwareType: string;
    protected apiName: string;

    protected authentication: IAuthentication;

    constructor(
        webRequestHandler: IWebRequestHandler,
        businessUnitRepository: BusinessUnitRepository,
        authentication: IAuthentication,
        hardwareType: string
    ) {
        super(webRequestHandler, businessUnitRepository);
        this.hardwareType = hardwareType;
        this.apiName = Api.hardware + "/" + this.hardwareType;
        this.authentication = authentication;
    }

    public getModulesForBusinessUnit(businessUnit: BusinessUnitDTO = null): Promise<TModRef[]> {
        const buId = businessUnit ? businessUnit.Id : this.authentication.businessUnitId;

        if (!buId) return Promise.reject<TModRef[]>("Not Authenticated");

        return this.webRequestHandler
            .createGetRequest<TModRef[]>(Api.hardware + "/" + buId + "/" + this.hardwareType)
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getModule(moduleId: string): Promise<TOwnedModRef> {
        return this.webRequestHandler
            .createGetRequest<TOwnedModRef>(this.apiName + "/" + moduleId)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public updateModule(module: TModRef): Promise<void> {
        // Make webrequest and strip of httpresponse message when returning an error
        return this.webRequestHandler
            .createPutRequest<void>(this.apiName + "/" + (<HardwareModule>module).HardwareId, module)
            .send()
            .then(() => {})
            .catch((e) => this.onError(e));
    }

    public newModule(module: TNewMod, moduleType: ModuleTypes, owner: BusinessUnitDTO = null): Promise<CreatedModule> {
        const buId = owner ? owner.Id : this.authentication.businessUnitId;

        if (!buId) return Promise.reject("Not Authenticated");

        // Make webrequest and strip of httpresponse message when returning an error
        return this.webRequestHandler
            .createPostRequest<CreatedModule>(
                Api.hardware + "/" + buId + "/" + this.hardwareType + "?moduleType=" + moduleType,
                module
            )
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }
}
//#endregion

//#region Hardware Specific Classes
@autoinject
export class DMADAQModuleRepository extends HardwareModuleRepository<
    FrontEndConnectorDTO,
    OwnedHardwareModuleDTOOfFrontEndConnectorDTO,
    NewFrontEndConnector
> {
    constructor(
        webRequestHandler: IWebRequestHandler,
        businessUnitRepository: BusinessUnitRepository,
        authentication: IAuthentication
    ) {
        super(webRequestHandler, businessUnitRepository, authentication, Api.frontEndConnectorDevice);
    }
}

@autoinject
export class MicrophoneArrayRackModuleRepository extends HardwareModuleRepository<
    MicrophoneArrayRackDTO,
    OwnedHardwareModuleDTOOfMicrophoneArrayRackDTO,
    NewMicrophoneArrayRack
> {
    constructor(
        webRequestHandler: IWebRequestHandler,
        businessUnitRepository: BusinessUnitRepository,
        authentication: IAuthentication
    ) {
        super(webRequestHandler, businessUnitRepository, authentication, Api.microphoneArrayRack);
    }

    /**
     * Get a microphone array rack, based on the mcirophoneArray Id's.
     * @param moduleIds
     */
    public getMicrophoneArrayRack(moduleIds: string[]): Promise<FrontEndDTO> {
        return this.webRequestHandler
            .createPostRequest<FrontEndDTO>(Api.hardware + "/" + Api.frontEnds + "/FromHardwareIds", moduleIds)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getMicrophoneArrays(rackId: string): Promise<MicrophoneArrayDTO[]> {
        return this.webRequestHandler
            .createGetRequest<MicrophoneArrayDTO[]>(this.apiName + "/" + rackId + "/MicrophoneArrays")
            .send()
            .then((response) => response.content)
            .catch((e) => this.onError(e));
    }

    public getWebcams(rackId: string): Promise<WebcamDTO[]> {
        return this.webRequestHandler
            .createGetRequest<WebcamDTO[]>(this.apiName + "/" + rackId + "/WebCams")
            .send()
            .then((response) => response.content)
            .catch((e) => this.onError(e));
    }

    public updateMarkerCollectionFromFile(moduleId: string, markerCollectionFileContents: string): Promise<void> {
        return this.webRequestHandler
            .createPutRequest<string>(
                this.apiName + "/" + moduleId + "/MarkerCollectionFromFile",
                markerCollectionFileContents
            )
            .withValue()
            .send()
            .then(() => {})
            .catch((e) => this.onError(e));
    }
}

@autoinject
export class MotionSystemModuleRepository extends HardwareModuleRepository<
    MotionDeviceDTO,
    OwnedHardwareModuleDTOOfMotionDeviceDTO,
    NewMotionDevice
> {
    constructor(
        webRequestHandler: IWebRequestHandler,
        businessUnitRepository: BusinessUnitRepository,
        authentication: IAuthentication
    ) {
        super(webRequestHandler, businessUnitRepository, authentication, Api.motionDevice);
    }
}

@autoinject
export class WebcamModuleRepository extends HardwareModuleRepository<
    WebcamDTO,
    OwnedHardwareModuleDTOOfWebcamDTO,
    NewWebcam
> {
    constructor(
        webRequestHandler: IWebRequestHandler,
        businessUnitRepository: BusinessUnitRepository,
        authentication: IAuthentication
    ) {
        super(webRequestHandler, businessUnitRepository, authentication, Api.webcamDevice);
    }
}

//#endregion
